/**
 * Required External Modules
 */
import App from "./App.vue";
import router from "./router";
import { createApp } from "vue";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import Toast from "primevue/toast";
import ProgressBar from "primevue/progressbar";
import { plugin, defaultConfig, createInput } from "@formkit/vue";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import i18n from "./plugins/i18n";
import { setupCalendar } from "v-calendar";
import formkitConfig from "../formkit.config";

/**
 * Import main css file
 */
import "./styles/main.scss";

/**
 * Import directives
 */
import directives from "./directives";

/**
 * Register Service Worker
 */
import "./registerServiceWorker";

/**
 * Import Components
 */
import ModalComponent from "./components/global/ModalComponent.vue";
import RLIconComponent from "./components/global/RLIconComponent.vue";
import SpinnerComponent from "./components/global/SpinnerComponent.vue";
import TooltipComponent from "./components/global/TooltipComponent.vue";
import PageTitleComponent from "./components/global/PageTitleComponent.vue";
import SearchInputComponent from "./components/forms/SearchInputComponent.vue";
import ChooseInputComponent from "./components/forms/ChooseInputComponent.vue";
import TreeSelectInputComponent from "./components/forms/TreeSelectInputComponent.vue";

/**
 * Client-side logger
 */
import { initLogger, logger, setUserIdResolver } from "./utils/logger";
import { useUserStore } from "./stores/userStore";

const app = createApp(App);

directives(app);

app
  .use(createPinia().use(piniaPluginPersistedstate))
  .use(router)
  .use(PrimeVue)
  .use(ToastService)
  .component("ProgressBar", ProgressBar)
  .component("Toast", Toast)
  .component("modal", ModalComponent)
  .component("rl-icon", RLIconComponent)
  .component("tooltip", TooltipComponent)
  .component("page-title", PageTitleComponent)
  .component("spinner-loader", SpinnerComponent)
  .use(
    plugin,
    defaultConfig({
      ...formkitConfig,
      inputs: {
        search: createInput(SearchInputComponent, {
          props: ["showIcon", "searchOptions", "placeholder"],
        }),
        choose: createInput(ChooseInputComponent, {
          props: ["size", "showIcon", "selectOptions", "placeholder"],
        }),
        treeSelect: createInput(TreeSelectInputComponent, {
          // 3. Define the props it can accept from the <FormKit> tag
          props: ['selectOptions'],
        }),
      },
    })
  )
  .use(i18n)
  .use(setupCalendar, {})
  .mount("#app");

// Wire client-side logging after Pinia is installed so the user store is reachable.
initLogger();
setUserIdResolver(() => {
  try {
    const userStore = useUserStore();
    return userStore.user?._id ?? null;
  } catch {
    return null;
  }
});

window.addEventListener("error", (event) => {
  logger.error("window.error", {
    message: event.message,
    filename: event.filename,
    line: event.lineno,
    col: event.colno,
    stack: event.error?.stack,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const reason: any = event.reason;
  logger.error("unhandled.promise", {
    reason: typeof reason === "string" ? reason : reason?.message ?? String(reason),
    stack: reason?.stack,
  });
});

app.config.errorHandler = (err, instance, info) => {
  const e = err as Error;
  logger.error("vue.error", {
    message: e?.message,
    stack: e?.stack,
    info,
    component: instance?.$options?.name || instance?.$options?.__name || null,
  });
  // Re-log to console so dev DX is unchanged.
  console.error("[vue.error]", err, info);
};
